import {
  Alert,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { CreateJobsRequest } from "protos/rtc/jobs";
import { DryRunPreview } from "../DryRunPreview";
import { formStateToCreateJobRequest, NewJobsFormState } from "../FormState";
import { NewJobsWizardFormProps } from "../NewJobsWizard";
import { PassSettingsSection } from "./PassSettingsSection";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { WizardStepButtonBar } from "../WizardStepButtonBar";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PreviewPathIcon from "portal/images/icons/conversion_path.svg?react";
import React, { useCallback, useMemo, useState } from "react";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";

type LaserWeedingConfigurePreviewFormProps = Pick<
  NewJobsWizardFormProps,
  "customerId" | "farmMapData" | "farmIdByFieldId" | "rigSearchResult"
>;

export const LaserWeedingConfigurePreviewForm: React.FC<
  LaserWeedingConfigurePreviewFormProps
> = ({ customerId, farmMapData, farmIdByFieldId, rigSearchResult }) => {
  const { t } = useTranslation();
  const { values, isValid } = useFormikContext<NewJobsFormState>();
  const [doDryRuns, dryRunResult] = rtcJobsApi.useCreateBulkJobsMutation({});

  const [viewingDryRunIndex, setViewingDryRunIndex] = useState(0);
  const nFailedDryRuns = useMemo(() => {
    const dryRunJobs = dryRunResult.data?.jobs ?? [];
    return dryRunJobs.filter((job) => job.creationFailed).length;
  }, [dryRunResult.data?.jobs]);
  const viewingDryRunJob = useMemo(() => {
    const dryRunJobs = dryRunResult.data?.jobs ?? [];
    return dryRunJobs[viewingDryRunIndex];
  }, [dryRunResult.data?.jobs, viewingDryRunIndex]);
  const viewingDryRunFarm = useMemo(() => {
    return viewingDryRunJob?.farmId
      ? farmMapData[viewingDryRunJob.farmId]
      : undefined;
  }, [farmMapData, viewingDryRunJob?.farmId]);
  const viewingDryRunField = useMemo(() => {
    return viewingDryRunFarm?.zonesById[viewingDryRunJob?.fieldId ?? ""];
  }, [viewingDryRunFarm?.zonesById, viewingDryRunJob?.fieldId]);

  const onClickPreview = useCallback(() => {
    setViewingDryRunIndex(0);
    const bulkJobReq: CreateJobsRequest = { jobRequests: [] };

    for (const fieldId of values.selectedFieldIds) {
      const farmId = farmIdByFieldId[fieldId];
      const farm = farmId ? farmMapData[farmId] : undefined;

      if (!farm) {
        throw new Error(
          `LaserWeedConfigurePreviewForm.onClickPreview: failed to get map data for ${fieldId}`
        );
      }

      bulkJobReq.jobRequests = formStateToCreateJobRequest(
        farm,
        // not localized because this string is only visible to carbon users
        () => `create jobs preview ${Date.now()}`,
        customerId,
        fieldId,
        true, // use dry runs for preview
        values,
        {}
      );
    }

    doDryRuns(bulkJobReq);
  }, [customerId, doDryRuns, farmIdByFieldId, farmMapData, values]);

  let previewSection: JSX.Element | undefined;

  if (dryRunResult.isError) {
    // network request failed
    previewSection = (
      <Alert severity="warning" className="w-full text-base rounded-lg">
        {t(
          "views.autotractor.jobCreation.step3ConfigurePreview.networkFailureAlert"
        )}
      </Alert>
    );
  } else if (dryRunResult.isSuccess) {
    // network request succeeded; we can display a preview (even if creating the
    // job itself failed)
    previewSection = (
      <>
        <Typography>
          {t("views.autotractor.jobCreation.step3ConfigurePreview.pathPreview")}
        </Typography>
        <Card className="rounded-lg p-4 w-full flex flex-row items-center justify-between">
          <div className="flex flex-col">
            <Typography>{viewingDryRunField?.name ?? ""}</Typography>
            <Typography variant="body2">
              {viewingDryRunFarm?.name ?? ""}
            </Typography>
          </div>
          <div className="flex flex-col gap-2 items-center">
            <div className="flex flex-row items-center">
              <IconButton
                disabled={viewingDryRunIndex === 0}
                onClick={() => setViewingDryRunIndex(viewingDryRunIndex - 1)}
              >
                <ArrowLeftIcon />
              </IconButton>
              <Typography>
                {viewingDryRunIndex + 1}/{dryRunResult.data.jobs.length}
              </Typography>
              <IconButton
                disabled={
                  viewingDryRunIndex === dryRunResult.data.jobs.length - 1
                }
                onClick={() => setViewingDryRunIndex(viewingDryRunIndex + 1)}
              >
                <ArrowRightIcon />
              </IconButton>
            </div>
            {nFailedDryRuns > 0 && (
              <div className="flex flex-row gap-2 items-center bg-warning p-2 rounded-md">
                <WarningIcon fontSize="inherit" />
                {nFailedDryRuns}
              </div>
            )}
          </div>
        </Card>
        {viewingDryRunJob && (
          <DryRunPreview
            dryRun={viewingDryRunJob}
            showJobData={"objective"}
            farmMapData={viewingDryRunFarm}
            configValueDisplayModules={[]}
          />
        )}
      </>
    );
  }

  // you should be able to continue to the next step without previewing, but
  // not if you don't have any rigs.
  const disableNext = !rigSearchResult || rigSearchResult.rigs.length === 0;

  return (
    <>
      <WizardStepButtonBar disableNext={disableNext} />
      <Paper
        variant="outlined"
        className="bg-inherit p-4 flex flex-col gap-4 rounded-lg"
      >
        <Typography variant="h5">
          {t("views.autotractor.jobCreation.step3ConfigurePreview.title")}
        </Typography>
        <Typography color="textSecondary">
          {t(
            "views.autotractor.jobCreation.step3ConfigurePreview.groundPrep.description"
          )}
        </Typography>
        <hr className="w-full" />
        <PassSettingsSection />
        <div className="flex flex-row items-center gap-4">
          {dryRunResult.isLoading ? (
            <Button
              variant="contained"
              startIcon={
                <CircularProgress variant="indeterminate" className="w-6 h-6" />
              }
              className="w-1/5"
              disabled={true}
            >
              {t(
                "views.autotractor.jobCreation.step3ConfigurePreview.generatingPath"
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<PreviewPathIcon />}
              className="w-1/5"
              disabled={!isValid}
              onClick={onClickPreview}
            >
              {t(
                "views.autotractor.jobCreation.step3ConfigurePreview.previewPath"
              )}
            </Button>
          )}
          <Typography variant="body2">
            {t(
              "views.autotractor.jobCreation.step3ConfigurePreview.pathPreviewSlowNote"
            )}
          </Typography>
        </div>
        {previewSection}
      </Paper>
      <WizardStepButtonBar disableNext={disableNext} />
    </>
  );
};
